import axios from "axios";
import type {
  Agreement,
  AgreementDetails,
  RegistrationAgreementClause,
  RegistrationScopeFilters,
} from "../../domain/entities/Agreement";
import { ApiConfig } from "../config/apiConfig";

const pickClauseLabel = (clause: Record<string, unknown>): string => {
  const label =
    clause.label ??
    clause.text ??
    clause.content ??
    clause.clause ??
    clause.statement ??
    clause.title ??
    "";

  return String(label).trim();
};

const pickClauseId = (
  clause: Record<string, unknown>,
  index: number,
): string => {
  const id = clause.id ?? clause.clause_id ?? clause.key ?? clause.code;
  return id ? String(id) : `registration-clause-${index}`;
};

const getClauseListFromResponse = (data: unknown): unknown[] => {
  if (Array.isArray(data)) return data;

  if (typeof data === "string") {
    try {
      return getClauseListFromResponse(JSON.parse(data));
    } catch {
      return data.trim() ? [{ label: data }] : [];
    }
  }

  if (data && typeof data === "object") {
    const body = data as Record<string, unknown>;
    const nested =
      body.clauses ??
      body.required_clauses ??
      body.registration_clauses ??
      body.items ??
      body.results;

    if (Array.isArray(nested)) return nested;
  }

  return [];
};

export class AgreementApi {
  async getRegisterAgreementIds(
    filters: RegistrationScopeFilters = {},
  ): Promise<Agreement[]> {
    const response = await axios.get(ApiConfig.AGREEMENT_IDS, {
      params: {
        language: "en",
        purpose: filters.purpose || undefined,
        institute_type: filters.institute_type || undefined,
        country_id: filters.country_id || undefined,
      },
    });

    return response.data.data.agreements
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((agreement: any) => ({
        id: agreement.id,
        type: agreement.type,
        title: agreement.title,
        version: agreement.version,
        applicable_user_types: agreement.applicable_user_types,
        created_at: agreement.created_at,
        institute_type: agreement.institute_type ?? null,
        country_id: agreement.country_id ?? null,
      }))
      .sort();
  }

  async getRegisterAgreementDetails(agreement_id: string): Promise<AgreementDetails> {
    const response = await axios.get(
      ApiConfig.agreementDetails(agreement_id),
    );

    const agreementDetails = {
      id: response.data.data.id,
      type: response.data.data.type,
      title: response.data.data.title,
      content: response.data.data.content,
      version: response.data.data.version,
      is_active: response.data.data.is_active,
      applicable_user_types: response.data.data.applicable_user_types,
      created_at: response.data.data.created_at,
      updated_at: response.data.data.updated_at,
      created_by: response.data.data.created_by,
    };

    return agreementDetails;
  }

  async getRequiredRegistrationClauses(
    filters: RegistrationScopeFilters = {},
  ): Promise<RegistrationAgreementClause[]> {
    const response = await axios.get(ApiConfig.REGISTRATION_REQUIRED_CLAUSES, {
      params: {
        institute_type: filters.institute_type || undefined,
        country_id: filters.country_id || undefined,
        purpose: filters.purpose || undefined,
      },
    });

    return getClauseListFromResponse(response.data?.data)
      .filter(
        (clause): clause is Record<string, unknown> =>
          !!clause && typeof clause === "object",
      )
      .map((clause, index) => ({
        id: pickClauseId(clause, index),
        label: pickClauseLabel(clause),
      }))
      .filter((clause) => clause.label.length > 0);
  }
}
